// frontend/src/components/book/atelier/framingLive.js
//
// CADRAGE EN DIRECT, SUR LE VRAI RENDU (phase 4a).
//
// Pendant qu'on fait glisser ou zoomer une photo, on ne dessine pas une
// imitation par-dessus la page : on modifie, dans le document de l'apercu (un
// iframe srcdoc, meme origine), les TROIS variables CSS que le rendu serveur
// pose sur l'<img> (--fx, --fy, --zoom). C'est exactement la meme regle CSS que
// celle de l'impression (voir .photo-frame img dans pageRenderer.js) : ce
// qu'on voit en glissant est ce qui sera imprime, sans decalage de geometrie.
// Au relachement, le nouveau cadrage est enregistre, le serveur re-rend la
// page, et le nouveau rendu (identique) remplace l'ancien.

import { isLeftPage } from '../../../utils/pageParity';

/** L'iframe de la page (ou de la face de couverture) dans le livre ouvert. */
export function findPageIframe(target) {
  if (!target || typeof document === 'undefined') return null;
  const selector = target.face
    ? `.atelier-flip-page[data-kind="${target.face === 'front' ? 'cover' : 'back-cover'}"] iframe`
    : `.atelier-flip-page[data-page-index="${target.pageIndex}"] iframe`;
  const all = document.querySelectorAll(selector);
  // Double tampon : pendant un echange, deux iframes coexistent ; le dernier est le plus recent.
  return all.length ? all[all.length - 1] : null;
}

/**
 * Les iframes a mettre a jour pendant un geste : la page visee et, pour une
 * photo sur DOUBLE PAGE (target.spread), sa page jumelle de l'autre cote du pli.
 */
export function findTargetIframes(target) {
  const main = findPageIframe(target);
  const list = main ? [main] : [];
  if (target && target.spread && Number.isInteger(target.pageIndex)) {
    const twin = findPageIframe({ pageIndex: isLeftPage(target.pageIndex) ? target.pageIndex + 1 : target.pageIndex - 1 });
    if (twin && twin !== main) list.push(twin);
  }
  return list;
}

/**
 * L'<img> de la photo (la n-ieme occurrence si elle est posee plusieurs fois).
 * `urls` : les adresses sous lesquelles le rendu peut l'embarquer (l'original ou
 * la variante redimensionnee pour l'ecran, selon l'apercu).
 */
export function findPhotoImg(iframe, urls, occurrence = 0) {
  let doc = null;
  try { doc = iframe && iframe.contentDocument; } catch (_error) { return null; }
  const wanted = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
  if (!doc || wanted.length === 0) return null;
  const images = [...doc.querySelectorAll('.photo-frame img')].filter((img) => wanted.includes(img.getAttribute('src')));
  return images[occurrence] || null;
}

/**
 * Taille du cadre en pixels DU DOCUMENT de l'iframe, et echelle d'affichage
 * (pixels ecran par pixel du document) — l'apercu est reduit par transform.
 */
export function measureFrame(iframe, img, fallbackRatio) {
  const frame = img.closest('.photo-frame') || img.parentElement;
  const rect = frame.getBoundingClientRect();
  const layoutWidth = iframe.offsetWidth || 1;
  const scale = iframe.getBoundingClientRect().width / layoutWidth || 1;
  const naturalRatio = img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : null;
  const iframeRect = iframe.getBoundingClientRect();
  return {
    // Position du cadre a l'ECRAN (pour retrouver ou se trouve le pointeur DANS le cadre).
    frameLeftPx: iframeRect.left + rect.left * scale,
    frameTopPx: iframeRect.top + rect.top * scale,
    frameW: rect.width,
    frameH: rect.height,
    scale,
    imageRatio: naturalRatio || fallbackRatio || null
  };
}

/** Pose le cadrage sur l'image, sans rien enregistrer. */
export function applyLiveFraming(img, framing) {
  img.style.setProperty('--fx', `${framing.focusX}%`);
  img.style.setProperty('--fy', `${framing.focusY}%`);
  img.style.setProperty('--zoom', String(framing.zoom));
}

/** Applique un cadrage en direct sur la photo d'une page ou d'une couverture (curseur de zoom du panneau). */
export function applyLiveToPhoto({ target, urls, occurrence = 0, framing }) {
  findTargetIframes(target).forEach((iframe) => {
    const img = findPhotoImg(iframe, urls, occurrence);
    if (img) applyLiveFraming(img, framing);
  });
}
